import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  getClientProductById,
  unassignProductFromClient,
} from 'api/generated/client-products/client-products';

import Breadcrumbs from 'components/partials/beadcrumbs/breadcrumbs';
import Heading from 'components/partials/heading/heading';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';
import Button from 'components/partials/button/button';
import Loader from 'components/partials/loader/loader';
import ProductDetails from 'containers/admin/clients/client/tabs-components/products-tab/product/product-details';
import ReportsAssignmentSettings from 'containers/admin/clients/client/tabs-components/products-tab/product/report-assignment/report-assignment';
import toast from 'components/partials/toast/toast';

import { useCurrentUser } from 'state/current-user/current-user.thunk';
import { useClient } from 'hooks/api/use-clients';

import Icons from 'assets/icons';

import { getBreadcrumbsForProduct } from 'containers/admin/clients/client/tabs-components/products-tab/product/product.helpers';
import { renderModal } from 'helpers/modal-render';

interface ClientProductProps {
  clientId: string | number | undefined;
  productId: string | number | undefined;
}

interface ProductRecord {
  id?: string | number;
  name?: string;
  description?: string;
  [key: string]: unknown;
}

const ClientProduct = ({
  clientId,
  productId,
}: ClientProductProps): React.JSX.Element => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);

  const [product, setProduct] = useState<ProductRecord | null>(null);

  const { data: currentClient } = useClient(clientId);
  const currentUser = useCurrentUser();
  const userId = currentUser.data?.oktaUserId ?? '';
  const currentUserData = currentUser?.data as
    | {
        internalAdmin?: boolean;
        reportAdmin?: boolean;
      }
    | null
    | undefined;
  const isInternalAdmin =
    !!currentUserData?.internalAdmin && !currentUserData?.reportAdmin;

  const handleUnassignProduct = async (): Promise<void> => {
    if (!clientId || !productId) return;
    setLoading(true);
    try {
      await unassignProductFromClient(
        userId,
        Number(clientId),
        Number(productId)
      );

      navigate(`/admin/clients/${clientId}/products`);
      toast.success({
        title: `Product ${product?.name} was successfully unassigned`,
      });
    } catch (error) {
      console.error('Error during unassigning product', error);
      toast.error({ title: 'Error during unassigning product' });
    } finally {
      setLoading(false);
    }
  };

  const handleUnassignButtonClick = (): void => {
    renderModal({
      headerText: `Are you sure you want to unassign this product from the ${currentClient?.name}?`,
      onConfirmCallback: handleUnassignProduct,
      onConfirmButtonLabel: 'Yes, unassign product',
    });
  };

  useEffect(() => {
    const fetchProduct = async (): Promise<void> => {
      if (!clientId || !productId) return;
      setInitialLoading(true);
      try {
        const dto = await getClientProductById(
          userId,
          Number(clientId),
          Number(productId)
        );

        setProduct(dto as ProductRecord);
      } catch (error) {
        console.error('Error while fetching product data', error);
        navigate(`/admin/clients/${clientId}/products`);
        toast.error({ title: 'Error loading product' });
      } finally {
        setInitialLoading(false);
      }
    };

    fetchProduct();
  }, [productId, userId, clientId]);

  return (
    <ResponsiveWrapper>
      <Loader loading={initialLoading} />
      {product && currentClient && (
        <>
          <Breadcrumbs
            breadcrumbs={getBreadcrumbsForProduct(
              currentClient,
              product?.name ?? ''
            )}
          />

          <div className="flex items-center justify-between mt-4 mb-5">
            <Heading variant="h1">{product?.name}</Heading>
            <Button
              variant="reserve-outlined"
              onClick={handleUnassignButtonClick}
              iconBefore={<Icons.Unassign />}
              loading={loading && !initialLoading}
              disabled={loading || isInternalAdmin}
            >
              Unassign Product
            </Button>
          </div>
          <div className="flex items-baseline mb-5 gap-12">
            <ProductDetails
              description={product?.description}
              customClass="w-1/3"
            />
            <ReportsAssignmentSettings
              clientId={clientId}
              productId={productId}
              parentLoading={loading || initialLoading}
            />
          </div>
        </>
      )}
    </ResponsiveWrapper>
  );
};

export default ClientProduct;
